import { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Plus } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import "react-big-calendar/lib/css/react-big-calendar.css";

const STATUS_COLOR = {
  programado: "#FF6B3D", en_curso: "#E0B400",
  finalizado: "#116A34", retraso: "#B91C1C",
};

export default function Calendario() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [embs, setEmbs] = useState([]);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const locale = lang === "en" ? enUS : es;
  const localizer = useMemo(() => dateFnsLocalizer({
    format, parse,
    startOfWeek: (d) => startOfWeek(d, { locale }),
    getDay, locales: { [lang]: locale },
  }), [lang, locale]);

  const messages = useMemo(() => ({
    allDay: t("cal.allDay"),
    previous: t("cal.previous"),
    next: t("cal.next"),
    today: t("cal.today"),
    month: t("cal.month"),
    week: t("cal.week"),
    day: t("cal.day"),
    agenda: t("cal.agenda"),
    date: t("cal.date"),
    time: t("cal.time"),
    event: t("cal.event"),
    noEventsInRange: t("cal.empty_range"),
    showMore: (n) => t("cal.show_more", { n }),
  }), [t]);

  useEffect(() => {
    (async () => {
      try {
        const [m, e] = await Promise.all([api.get("/mantenimientos"), api.get("/embarcaciones")]);
        setItems(m.data); setEmbs(e.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const embMap = useMemo(() => Object.fromEntries(embs.map((x) => [x.id, x])), [embs]);
  const events = useMemo(() => items.map((m) => {
    const startDt = new Date(m.start_at);
    // Si no hay fin programado, mostramos bloque de 1h para que sea visible en el grid
    const endDt = m.end_at ? new Date(m.end_at) : new Date(startDt.getTime() + 60 * 60 * 1000);
    return {
      id: m.id,
      title: embMap[m.embarcacion_id]?.name || t("cal.event"),
      start: startDt,
      end: endDt,
      resource: m,
    };
  }), [items, embMap, t]);

  const eventStyleGetter = useCallback((event) => ({
    style: {
      backgroundColor: STATUS_COLOR[event.resource.status] || "#64748B",
      borderRadius: 8, border: "none", color: "white",
      padding: "2px 6px", fontWeight: 600, fontSize: 12,
    },
  }), []);

  return (
    <div className="px-5 pt-8 pb-24 sns-fade-up max-w-7xl mx-auto" data-testid="page-calendario">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white border border-[var(--border)] flex items-center justify-center">
          <ArrowLeft size={18} />
        </button>
        <h1 className="sns-title text-2xl flex-1">{t("cal.title")}</h1>
        <button onClick={() => nav("/nuevo-mantenimiento")} className="sns-fab w-11 h-11 flex items-center justify-center" data-testid="btn-new-cal">
          <Plus size={20} strokeWidth={2.5} />
        </button>
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(STATUS_COLOR).map(([k, c]) => (
          <span key={k} className="sns-pill text-white" style={{ background: c }}>
            {t(`cal.legend.${k}`)}
          </span>
        ))}
      </div>

      <div className="sns-card p-3 md:p-5" style={{ height: "72vh", minHeight: 540 }}>
        {loading ? <div className="h-full flex items-center justify-center text-slate-400">{t("common.loading")}</div> : (
          <Calendar
            localizer={localizer} culture={lang} messages={messages}
            events={events}
            view={view} onView={setView}
            date={date} onNavigate={setDate}
            views={["month", "week", "day", "agenda"]}
            eventPropGetter={eventStyleGetter}
            style={{ height: "100%" }}
            onSelectEvent={(ev) => nav(`/trabajos/${ev.id}`)}
            onSelectSlot={() => nav("/nuevo-mantenimiento")}
            selectable
            popup
          />
        )}
      </div>
    </div>
  );
}
